if (!customElements.get('coverax-shipping-countdown')) {
  customElements.define(
    'coverax-shipping-countdown',
    class CoveraxShippingCountdown extends HTMLElement {
      connectedCallback() {
        this.timezone = this.dataset.timezone || 'Europe/Berlin';
        this.cutoffHour = Number(this.dataset.cutoffHour || 18);
        this.cutoffMinute = Number(this.dataset.cutoffMinute || 0);
        this.shipDays = (this.dataset.shipDays || '1,2,3,4,5')
          .split(',')
          .map((value) => value.trim())
          .filter((value) => value !== '')
          .map((value) => Number(value))
          .filter((value) => Number.isInteger(value) && value >= 0 && value <= 6);
        this.showDays = this.dataset.showDays === 'true';
        this.locale = this.dataset.locale || document.documentElement.lang || 'de';
        this.kickerToday = this.dataset.kickerToday || '';
        this.kickerNext = this.dataset.kickerNext || '';
        this.kickerUrgent = this.dataset.kickerUrgent || '';
        this.suffixToday = this.dataset.suffixToday || '';
        this.suffixNext = this.dataset.suffixNext || '';
        this.noteToday = this.dataset.noteToday || '';
        this.noteNext = this.dataset.noteNext || '';
        this.noteUrgent = this.dataset.noteUrgent || '';
        this.kickerText = this.querySelector('[data-ship-kicker-text]');
        this.suffixEl = this.querySelector('[data-ship-suffix]');
        this.noteEl = this.querySelector('[data-ship-note]');
        this.leadEl = this.querySelector('[data-ship-lead]');
        this.compactEl = this.querySelector('[data-ship-compact]');
        this.compactMinutes = this.querySelector('[data-compact-minutes]');
        this.compactSeconds = this.querySelector('[data-compact-seconds]');
        this.srEl = this.querySelector('[data-ship-sr]');
        this.dayUnit = this.querySelector('[data-unit="days"]');
        this.dayValue = this.dayUnit?.querySelector('[data-value]');
        this.hourValue = this.querySelector('[data-unit="hours"] [data-value]');
        this.minuteValue = this.querySelector('[data-unit="minutes"] [data-value]');
        this.secondValue = this.querySelector('[data-unit="seconds"] [data-value]');
        this.stockDetails = this.closest('details.coverax-pdp__stock-details');
        this.stockExtra = this.stockDetails?.querySelector('[data-pdp-stock-extra]');
        this.stockStatus = this.stockDetails?.querySelector('[data-pdp-stock-status]');
        this.stockToday = this.stockDetails?.dataset.stockToday || '';
        this.stockNext = this.stockDetails?.dataset.stockNext || '';
        this.stockUrgent = this.stockDetails?.dataset.stockUrgent || '';
        this.tick = this.tick.bind(this);
        this.onVisibility = () => {
          if (document.hidden) this.stopTimer();
          else this.startTimer();
        };
        this.tick();
        this.startTimer();
        document.addEventListener('visibilitychange', this.onVisibility);
      }

      disconnectedCallback() {
        this.stopTimer();
        if (this.onVisibility) document.removeEventListener('visibilitychange', this.onVisibility);
      }

      startTimer() {
        this.stopTimer();
        if (document.hidden) return;
        this.timerId = window.setInterval(this.tick, 1000);
      }

      stopTimer() {
        if (this.timerId) {
          window.clearInterval(this.timerId);
          this.timerId = 0;
        }
      }

      tick() {
        const target = this.getTarget();
        if (!target) {
          this.hideTimer();
          return;
        }

        const remaining = Math.max(0, target.at - Date.now());
        if (remaining <= 0) {
          this.hideTimer();
          return;
        }

        this.hidden = false;
        const sameDay = Boolean(target.sameDay);
        this.syncDisclosure(sameDay);
        const urgent = remaining <= 60 * 60 * 1000;
        const dayName = this.formatWeekday(target.at);

        this.classList.toggle('is-next', !sameDay);
        this.classList.toggle('is-urgent', urgent && sameDay);

        const totalSec = Math.floor(remaining / 1000);
        const days = Math.floor(totalSec / 86400);
        const hours = Math.floor((totalSec % 86400) / 3600);
        const minutes = Math.floor((totalSec % 3600) / 60);
        const seconds = totalSec % 60;
        const showUrgent = urgent && sameDay;
        const showDayUnit = this.showDays && days > 0 && !showUrgent;

        if (this.dayUnit) this.dayUnit.hidden = !showDayUnit;
        if (this.dayValue) this.dayValue.textContent = this.pad(days);
        if (this.hourValue) this.hourValue.textContent = this.pad(showDayUnit ? hours : days * 24 + hours);
        if (this.minuteValue) this.minuteValue.textContent = this.pad(minutes);
        if (this.secondValue) this.secondValue.textContent = this.pad(seconds);
        if (this.srEl) this.srEl.hidden = showUrgent;
        if (this.compactEl) this.compactEl.hidden = !showUrgent;
        if (this.compactMinutes) this.compactMinutes.textContent = String(minutes);
        if (this.compactSeconds) this.compactSeconds.textContent = String(seconds);

        if (this.kickerText) {
          if (showUrgent && this.kickerUrgent) {
            this.kickerText.textContent = this.kickerUrgent;
          } else if (sameDay) {
            this.kickerText.textContent = this.kickerToday;
          } else {
            this.kickerText.textContent = this.withDay(this.kickerNext, dayName);
          }
        }

        if (this.suffixEl) {
          this.suffixEl.textContent = sameDay
            ? this.suffixToday
            : this.withDay(this.suffixNext, dayName);
        }

        if (this.noteEl) {
          if (showUrgent && this.noteUrgent) {
            this.noteEl.textContent = this.noteUrgent;
          } else if (sameDay) {
            this.noteEl.textContent = this.noteToday;
          } else {
            this.noteEl.textContent = this.withDay(this.noteNext, dayName);
          }
        }

        if (this.srEl) {
          const parts = [];
          if (showDayUnit) {
            parts.push(
              `${this.pad(days)} ${this.dayUnit?.querySelector('.coverax-pdp__ship-timer-label')?.textContent || ''}`.trim()
            );
          }
          parts.push(
            `${this.hourValue?.textContent || '00'} ${this.querySelector('[data-unit="hours"] .coverax-pdp__ship-timer-label')?.textContent || ''}`.trim()
          );
          parts.push(
            `${this.pad(minutes)} ${this.querySelector('[data-unit="minutes"] .coverax-pdp__ship-timer-label')?.textContent || ''}`.trim()
          );
          parts.push(
            `${this.pad(seconds)} ${this.querySelector('[data-unit="seconds"] .coverax-pdp__ship-timer-label')?.textContent || ''}`.trim()
          );
          this.srEl.setAttribute('aria-label', parts.join(', '));
        }

        this.updateStockLabel(sameDay);
      }

      hideTimer() {
        this.hidden = true;
        this.syncStockDetails(false);
        this.updateUspShipTitles(false);
        if (!this.stockExtra || !this.stockDetails) return;
        const stock = this.stockDetails.querySelector('[data-pdp-stock]');
        if (stock?.classList.contains('is-unavailable')) return;
        if (this.stockNext) this.stockExtra.textContent = this.stockNext;
      }

      syncStockDetails(visible) {
        if (!this.stockDetails) return;
        this.stockDetails.classList.toggle('is-timer-hidden', !visible);
        this.stockDetails.classList.toggle('is-static', !visible);
        if (!visible) this.stockDetails.open = false;
      }

      syncDisclosure(expandable) {
        if (!this.stockDetails) return;
        this.stockDetails.classList.remove('is-timer-hidden');
        this.stockDetails.classList.toggle('is-static', !expandable);
        if (!expandable) this.stockDetails.open = false;
      }

      updateStockLabel(sameDay) {
        if (this.stockExtra && this.stockDetails) {
          const stock = this.stockDetails.querySelector('[data-pdp-stock]');
          if (!stock?.classList.contains('is-unavailable')) {
            const text = sameDay ? this.stockToday : this.stockNext;
            if (text) this.stockExtra.textContent = text;
          }
        }
        this.updateUspShipTitles(sameDay);
      }

      updateUspShipTitles(sameDay) {
        document.querySelectorAll('[data-usp-ship-title]').forEach((el) => {
          const text = sameDay ? el.dataset.uspShipToday : el.dataset.uspShipNext;
          if (text) el.textContent = text;
        });
        document.querySelectorAll('[data-usp-ship-icon]').forEach((el) => {
          const today = el.querySelector('[data-usp-ship-icon-today]');
          const next = el.querySelector('[data-usp-ship-icon-next]');
          if (today) today.hidden = !sameDay;
          if (next) next.hidden = sameDay;
        });
      }

      getTarget() {
        if (!this.shipDays.length) return null;

        const now = new Date();
        const parts = this.zonedParts(now);
        const todayWeekday = this.weekdayFromYmd(parts.year, parts.month, parts.day);

        if (this.shipDays.includes(todayWeekday)) {
          const cutoff = this.zonedTimeToUtc(
            parts.year,
            parts.month,
            parts.day,
            this.cutoffHour,
            this.cutoffMinute
          );
          if (now.getTime() < cutoff) {
            return { at: cutoff, sameDay: true };
          }
        }

        for (let add = 1; add <= 8; add += 1) {
          const next = this.addCivilDays(parts.year, parts.month, parts.day, add);
          const weekday = this.weekdayFromYmd(next.year, next.month, next.day);
          if (!this.shipDays.includes(weekday)) continue;
          return {
            at: this.zonedTimeToUtc(next.year, next.month, next.day, this.cutoffHour, this.cutoffMinute),
            sameDay: false,
          };
        }

        return null;
      }

      zonedParts(date) {
        const format = new Intl.DateTimeFormat('en-US', {
          timeZone: this.timezone,
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hourCycle: 'h23',
        });
        const values = Object.fromEntries(format.formatToParts(date).map((part) => [part.type, part.value]));
        return {
          year: Number(values.year),
          month: Number(values.month),
          day: Number(values.day),
          hour: Number(values.hour),
          minute: Number(values.minute),
          second: Number(values.second),
        };
      }

      zonedTimeToUtc(year, month, day, hour, minute, second = 0) {
        const utcGuess = Date.UTC(year, month - 1, day, hour, minute, second);
        const asZone = this.zonedParts(new Date(utcGuess));
        const asUtc = Date.UTC(asZone.year, asZone.month - 1, asZone.day, asZone.hour, asZone.minute, asZone.second);
        return utcGuess - (asUtc - utcGuess);
      }

      addCivilDays(year, month, day, amount) {
        const date = new Date(Date.UTC(year, month - 1, day + amount));
        return {
          year: date.getUTCFullYear(),
          month: date.getUTCMonth() + 1,
          day: date.getUTCDate(),
        };
      }

      weekdayFromYmd(year, month, day) {
        return new Date(Date.UTC(year, month - 1, day)).getUTCDay();
      }

      formatWeekday(timestamp) {
        const name = new Intl.DateTimeFormat(this.locale, {
          weekday: 'long',
          timeZone: this.timezone,
        }).format(new Date(timestamp));
        return name.charAt(0).toUpperCase() + name.slice(1);
      }

      withDay(template, dayName) {
        return String(template || '').replace(/__DAY__/g, dayName);
      }

      pad(value) {
        return String(Math.max(0, value)).padStart(2, '0');
      }
    }
  );
}
